import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaDownload } from 'react-icons/fa';
import api from '../services/api';
import './PurchaseOrders.css';

const PurchaseOrders = ({ currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Search for jobs by job number
  const handleSearch = async (search) => {
    setSearchTerm(search);

    if (!search.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await api.get('/purchase-orders/search', {
        params: { search: search.trim() }
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching jobs:', error);
      toast.error('Failed to search jobs');
    } finally {
      setIsSearching(false);
    }
  };

  // Load purchase orders for selected job
  const loadPurchaseOrders = async (jobId) => {
    setIsLoading(true);
    try {
      const response = await api.get('/purchase-orders', {
        params: { job_id: jobId }
      });
      setPurchaseOrders(response.data);
    } catch (error) {
      console.error('Error loading purchase orders:', error);
      toast.error('Failed to load purchase orders');
    } finally {
      setIsLoading(false);
    }
  };

  // Select a job and load its purchase orders
  const handleSelectJob = async (job) => {
    setSelectedJob(job);
    setSearchTerm('');
    setSearchResults([]);
    await loadPurchaseOrders(job.id);
  };

  // Auto-select job if navigating back from edit/create page
  useEffect(() => {
    if (location.state?.job) {
      handleSelectJob(location.state.job);
      // Clear the state to prevent re-selecting on future navigations
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Navigate to create new PO
  const handleCreateNew = () => {
    if (!selectedJob) {
      toast.error('Please select a job first');
      return;
    }
    navigate('/purchase-orders/new', { state: { job: selectedJob } });
  };

  // Navigate to edit existing PO
  const handleEdit = (po) => {
    navigate(`/purchase-orders/${po.id}`, { state: { purchaseOrder: po, job: selectedJob } });
  };

  // Download Purchase Order as PDF (using window.print)
  const handleDownloadPDF = async (po) => {
    try {
      // Fetch full PO data
      const response = await api.get(`/purchase-orders/${po.id}`);
      const fullPO = response.data;

      // Generate line items HTML
      const lineItemRows = fullPO.line_items?.map(item => `
        <tr>
          <td style="text-align: center;">${item.qty}</td>
          <td>${item.description}</td>
          <td style="text-align: right;">${item.price ? '$' + parseFloat(item.price).toFixed(2) : ''}</td>
          <td></td>
        </tr>
      `).join('') || '';

      // Add empty rows to fill the form (minimum 12 rows total)
      const currentRows = fullPO.line_items?.length || 0;
      const minRows = 12;
      const emptyRowsNeeded = Math.max(0, minRows - currentRows);
      const emptyRows = Array(emptyRowsNeeded).fill('<tr><td>&nbsp;</td><td></td><td></td><td></td></tr>').join('');

      // Create print window with proper HTML structure
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Purchase Order ${fullPO.po_number}</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: Arial, sans-serif; padding: 30px; background: #fff; color: #333; }

            .action-buttons {
              display: flex;
              gap: 10px;
              margin-bottom: 20px;
              justify-content: center;
            }
            .action-buttons button {
              padding: 10px 30px;
              font-size: 16px;
              font-weight: bold;
              border: none;
              border-radius: 5px;
              cursor: pointer;
              transition: background-color 0.2s;
            }
            .btn-print {
              background-color: #007bff;
              color: white;
            }
            .btn-print:hover {
              background-color: #0056b3;
            }
            .btn-cancel {
              background-color: #6c757d;
              color: white;
            }
            .btn-cancel:hover {
              background-color: #545b62;
            }

            .po-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #000; padding-bottom: 10px; margin-bottom: 8px; }
            .po-company-name { font-size: 28px; font-weight: bold; color: #000; line-height: 1.1; }
            .po-company-subtitle { font-size: 18px; font-weight: bold; color: #000; }
            .po-title { text-align: right; }
            .po-title-text { font-size: 20px; font-weight: bold; }
            .po-number { font-size: 14px; margin-top: 5px; }
            .po-company-info { font-size: 9px; margin: 8px 0 20px 0; line-height: 1.5; }
            .po-info-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            .po-info-table td { padding: 5px 8px; border: 1px solid #000; font-size: 10px; }
            .po-info-table td:nth-child(odd) { font-weight: bold; width: 20%; background: #fff; }
            .po-info-table td:nth-child(even) { width: 30%; }
            .po-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            .po-table th { background: #e0e0e0; font-weight: bold; padding: 6px 8px; border: 1px solid #000; text-align: left; font-size: 10px; }
            .po-table td { padding: 5px 8px; border: 1px solid #000; font-size: 10px; }
            .po-total-row td { font-weight: bold; border-top: 2px solid #000; }
            .po-footer { display: flex; gap: 0; }
            .po-footer-box { flex: 1; border: 1px solid #000; padding: 12px; font-size: 9px; line-height: 1.5; }
            .po-footer-title { font-weight: bold; margin-bottom: 8px; }

            @media print {
              .action-buttons { display: none; }
              body { padding: 20px; }
              @page { margin: 0.5in; size: letter; }
            }
          </style>
        </head>
        <body>
          <div class="action-buttons">
            <button class="btn-print" onclick="window.print()">Print</button>
            <button class="btn-cancel" onclick="window.close()">Cancel</button>
          </div>

          <div class="po-header">
            <div>
              <div class="po-company-name">SCOTT</div>
              <div class="po-company-subtitle">OVERHEAD DOOR</div>
            </div>
            <div class="po-title">
              <div class="po-title-text">PURCHASE ORDER</div>
              <div class="po-number">N° ${fullPO.po_number}</div>
            </div>
          </div>

          <div class="po-company-info">
            10161 N. Blanchard St. - Suite A<br/>
            Riverside, CA 92509<br/>
            T: (714) 636-9318 - F: (714) 636-9073<br/>
            Lic. # 872640
          </div>

          <table class="po-info-table">
            <tr>
              <td>TO:</td>
              <td>${fullPO.to_field || ''}</td>
              <td>DATE:</td>
              <td>${fullPO.date ? new Date(fullPO.date).toLocaleDateString('en-US', {month: '2-digit', day: '2-digit', year: '2-digit'}) : ''}</td>
            </tr>
            <tr>
              <td>CITY, STATE, ZIP:</td>
              <td></td>
              <td>TERMS:</td>
              <td></td>
            </tr>
            <tr>
              <td>SHIP TO:</td>
              <td>${fullPO.to_field || ''}</td>
              <td>HOW SHIPPED:</td>
              <td>${fullPO.how_shipped || ''}</td>
            </tr>
            <tr>
              <td>ADDRESS:</td>
              <td></td>
              <td>F.O.B.:</td>
              <td></td>
            </tr>
            <tr>
              <td>CITY, STATE, ZIP:</td>
              <td></td>
              <td>P.O. #:</td>
              <td>${fullPO.job?.job_number || selectedJob?.job_number || ''}</td>
            </tr>
          </table>

          <table class="po-table">
            <thead>
              <tr>
                <th style="width: 80px; text-align: center;">QUANTITY</th>
                <th>DESCRIPTION</th>
                <th style="width: 100px; text-align: right;">PRICE</th>
                <th style="width: 80px; text-align: center;">UNIT</th>
              </tr>
            </thead>
            <tbody>
              ${lineItemRows}
              ${emptyRows}
              <tr class="po-total-row">
                <td></td>
                <td style="text-align: right; font-weight: bold;">Total</td>
                <td style="text-align: right; font-weight: bold;">$${parseFloat(fullPO.total_price || 0).toFixed(2)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>

          <div class="po-footer">
            <div class="po-footer-box">
              ${fullPO.notes ? `<div class="po-footer-title">NOTES:</div><div>${fullPO.notes}</div>` : `
              <div class="po-footer-title">IMPORTANT</div>
              <div>
                PURCHASE ORDER MUST APPEAR<br/>
                ON ALL INVOICES, PACKAGES, BILLS OF<br/>
                LADING, NOTICES OF DELIVERIES. IF YOU<br/>
                ARE UNABLE TO COMPLETE ORDER BY<br/>
                DATE SPECIFIED
              </div>
              `}
            </div>
            <div class="po-footer-box">
              PLEASE SEND _____ COPIES OF YOUR<br/>
              INVOICE WITH ORIGINAL BILL OF LADING.<br/><br/>
              PURCHASER AGENT:
            </div>
          </div>
        </body>
        </html>
      `);
      printWindow.document.close();

    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  // Clear selection
  const handleClearSelection = () => {
    setSelectedJob(null);
    setPurchaseOrders([]);
  };

  return (
    <div className="purchase-orders-container">
      <div className="page-header">
        <h1>Purchase Orders</h1>
      </div>

      {/* Search Section */}
      <div className="search-section card">
        <h3>Search by Job Number</h3>
        <div className="search-input-wrapper">
          <input
            type="text"
            className="form-control"
            placeholder="Enter job number..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {isSearching && <span className="search-spinner">Searching...</span>}
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="search-results">
            {searchResults.map((job) => (
              <div
                key={job.id}
                className="search-result-item"
                onClick={() => handleSelectJob(job)}
              >
                <div className="job-number">{job.job_number}</div>
                <div className="job-details">
                  <span className="customer-name">{job.customer_name}</span>
                  <span className="job-status badge">{job.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected Job Section */}
      {selectedJob && (
        <div className="selected-job-section card">
          <div className="selected-job-header">
            <div>
              <h3>Job: {selectedJob.job_number}</h3>
              <p className="customer-name">{selectedJob.customer_name}</p>
            </div>
            <div className="action-buttons">
              <button className="btn btn-primary" onClick={handleCreateNew}>
                Create New Purchase Order
              </button>
              <button className="btn btn-secondary" onClick={handleClearSelection}>
                Clear Selection
              </button>
            </div>
          </div>

          {/* Purchase Orders List */}
          <div className="purchase-orders-list">
            <h4>Existing Purchase Orders</h4>
            {!isLoading && purchaseOrders.length === 0 && (
              <div className="no-purchase-orders">
                <p>No purchase orders found for this job.</p>
                <p>Click "Create New Purchase Order" to get started.</p>
              </div>
            )}
            {!isLoading && purchaseOrders.length > 0 && (
              <>
                {/* Desktop Table View */}
                <table className="table purchase-orders-table">
                  <thead>
                    <tr>
                      <th>PO Number</th>
                      <th>Date</th>
                      <th>To</th>
                      <th>How Shipped</th>
                      <th>Total</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseOrders.map((po) => (
                      <tr key={po.id}>
                        <td>{po.po_number}</td>
                        <td>{new Date(po.date).toLocaleDateString()}</td>
                        <td>{po.to_field}</td>
                        <td>{po.how_shipped}</td>
                        <td>${parseFloat(po.total_price).toFixed(2)}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleEdit(po)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleDownloadPDF(po)}
                          >
                            <FaDownload size={14} className="me-1" />
                            Download PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Mobile Card View */}
                <div className="purchase-orders-mobile-cards">
                  {purchaseOrders.map((po) => (
                    <div key={po.id} className="purchase-order-mobile-card">
                      <div className="po-header">
                        <div className="po-number">{po.po_number}</div>
                        <div className="po-total">${parseFloat(po.total_price).toFixed(2)}</div>
                      </div>
                      <div className="po-details">
                        <div className="po-detail-row">
                          <span className="po-detail-label">Date:</span>
                          <span>{new Date(po.date).toLocaleDateString()}</span>
                        </div>
                        <div className="po-detail-row">
                          <span className="po-detail-label">To:</span>
                          <span>{po.to_field}</span>
                        </div>
                        <div className="po-detail-row">
                          <span className="po-detail-label">How Shipped:</span>
                          <span>{po.how_shipped}</span>
                        </div>
                      </div>
                      <div className="po-actions">
                        <button
                          className="btn btn-primary"
                          onClick={() => handleEdit(po)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={() => handleDownloadPDF(po)}
                        >
                          <FaDownload size={16} />
                          Download PDF
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrders;
